import { useEffect, useState } from 'react'
import { X, Save } from 'lucide-react'
import type { Campaign } from '@/modules/promo/model/types'
import { CATEGORIES } from '@/modules/products/config/products.config'
import { useAdminPromoStore } from '../../../state/promoStore'
import { useToast } from '../../components/Toast'

const GRADIENTS = [
  { value: 'from-brand-500 to-brand-700',  label: 'Xanh dương' },
  { value: 'from-accent-500 to-accent-600', label: 'Xanh lá' },
  { value: 'from-orange-500 to-red-600',    label: 'Cam đỏ' },
  { value: 'from-purple-500 to-pink-600',   label: 'Tím hồng' },
  { value: 'from-slate-700 to-slate-900',   label: 'Đen tối' },
]

interface CampaignFormDialogProps {
  open: boolean
  editing: Campaign | null
  onClose: () => void
}

const EMPTY: Omit<Campaign, 'id'> = {
  title: '', highlight: '', description: '', discount: '',
  image: '', endDate: '', ctaTo: '/danh-muc/xe-may-dien',
  gradient: 'from-brand-500 to-brand-700',
}

export function CampaignFormDialog({ open, editing, onClose }: CampaignFormDialogProps) {
  const add = useAdminPromoStore((s) => s.addCampaign)
  const update = useAdminPromoStore((s) => s.updateCampaign)
  const push = useToast((s) => s.push)

  const [v, setV] = useState<Omit<Campaign, 'id'>>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setErrors({})
    setV(editing ? { ...editing } : EMPTY)
  }, [open, editing])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const upd = <K extends keyof typeof v>(k: K, val: typeof v[K]) => {
    setV((s) => ({ ...s, [k]: val }))
    setErrors((e) => ({ ...e, [k as string]: '' }))
  }

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const handleSave = async () => {
    const errs: Record<string, string> = {}
    if (!v.title.trim())       errs.title = 'Vui lòng nhập tên chiến dịch'
    if (!v.highlight.trim())   errs.highlight = 'Vui lòng nhập dòng highlight'
    if (!v.description.trim()) errs.description = 'Vui lòng nhập mô tả'
    if (!v.endDate.trim())     errs.endDate = 'Vui lòng nhập ngày kết thúc'
    if (!v.ctaTo.trim())       errs.ctaTo = 'Vui lòng nhập URL đích'
    if (Object.keys(errs).length) { setErrors(errs); return }

    if (editing) {
      await update(editing.id, v)
      push('success', `Đã cập nhật "${v.title}"`)
    } else {
      await add(v)
      push('success', `Đã thêm chiến dịch "${v.title}"`)
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="h-14 px-5 border-b border-border flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-text">{editing ? 'Sửa chiến dịch' : 'Thêm chiến dịch mới'}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Preview */}
          <div className={`relative rounded-xl overflow-hidden bg-gradient-to-br ${v.gradient} text-white p-5`}>
            <div className="absolute inset-0 opacity-20">
              {v.image && <img src={v.image} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />}
            </div>
            <div className="relative">
              <div className="inline-block bg-white/20 backdrop-blur-sm text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase">
                {v.title || 'Tên chiến dịch'}
              </div>
              <div className="mt-2 text-xl font-bold">{v.highlight || 'Dòng highlight lớn'}</div>
              <div className="mt-1 text-sm text-white/90 line-clamp-2">{v.description || 'Mô tả ngắn...'}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tên chiến dịch <span className="text-danger">*</span></div>
              <input value={v.title} onChange={(e) => upd('title', e.target.value)} className={inputCls(errors.title)} placeholder="Siêu sale tháng 11" />
              {errors.title && <div className="text-xs text-danger mt-1">{errors.title}</div>}
            </label>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Ngày kết thúc <span className="text-danger">*</span></div>
              <input value={v.endDate} onChange={(e) => upd('endDate', e.target.value)} className={inputCls(errors.endDate)} placeholder="30/11/2026" />
              {errors.endDate && <div className="text-xs text-danger mt-1">{errors.endDate}</div>}
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Dòng highlight <span className="text-danger">*</span></div>
            <input value={v.highlight} onChange={(e) => upd('highlight', e.target.value)} className={inputCls(errors.highlight)} placeholder="Giảm đến 2.000.000đ" />
            {errors.highlight && <div className="text-xs text-danger mt-1">{errors.highlight}</div>}
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Mô tả ngắn <span className="text-danger">*</span></div>
            <textarea
              value={v.description}
              onChange={(e) => upd('description', e.target.value)}
              rows={2}
              className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
              placeholder="Áp dụng cho các dòng xe máy điện cao cấp..."
            />
            {errors.description && <div className="text-xs text-danger mt-1">{errors.description}</div>}
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Nhãn giảm giá hiển thị</div>
              <input value={v.discount} onChange={(e) => upd('discount', e.target.value)} className={inputCls()} placeholder="2.000.000đ / 0% lãi suất" />
            </label>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Danh mục đích (CTA) <span className="text-danger">*</span></div>
              <select value={v.ctaTo} onChange={(e) => upd('ctaTo', e.target.value)} className={inputCls(errors.ctaTo)}>
                <option value="/khuyen-mai">Trang khuyến mãi</option>
                {CATEGORIES.map((c) => <option key={c.slug} value={`/danh-muc/${c.slug}`}>Danh mục: {c.label}</option>)}
              </select>
              {errors.ctaTo && <div className="text-xs text-danger mt-1">{errors.ctaTo}</div>}
            </label>
          </div>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Ảnh nền (URL)</div>
            <input value={v.image} onChange={(e) => upd('image', e.target.value)} className={inputCls()} placeholder="https://..." />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Màu gradient</div>
            <div className="grid grid-cols-5 gap-2">
              {GRADIENTS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => upd('gradient', g.value)}
                  className={[
                    `h-16 rounded-lg bg-gradient-to-br ${g.value} border-2 transition`,
                    v.gradient === g.value ? 'border-text ring-2 ring-brand-200' : 'border-transparent',
                  ].join(' ')}
                  title={g.label}
                />
              ))}
            </div>
          </label>
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-border flex items-center justify-end gap-3 shrink-0">
          <button onClick={onClose} className="h-10 px-4 rounded-md border border-border text-text hover:bg-surface text-sm font-medium">Hủy</button>
          <button onClick={handleSave} className="h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {editing ? 'Lưu thay đổi' : 'Tạo chiến dịch'}
          </button>
        </div>
      </div>
    </div>
  )
}
